import { useCallback, useMemo, useRef, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import { useI18n } from '../lib/i18n';
import { validateAnswers, type AnswerValue } from '@shared/forms';
import { FormQuestion, type FormField, type FormSection } from './FormQuestion';

/**
 * "Request a service" — the card on a business's public page (0053).
 *
 * FOR WORK THAT NEEDS NO APPOINTMENT. The customer says who they are
 * and how to reach them, answers the business's own intake questions,
 * and is done: no date, no time, no slot. The business's questions are
 * an ordinary intake form of the type 'service_request', rendered by
 * the same component the secure-link form page uses, so a question
 * looks the same wherever a customer meets it.
 *
 * The name, phone and email are the page's own fields rather than the
 * form's, exactly as a booking's are — which is what lets the same
 * questions serve a team member taking a request by phone, where the
 * customer is picked from the list instead.
 */

interface Loaded {
  business: { name: string; slug: string };
  form: {
    id: string; name: string; instructions: string | null; privacy_note: string; version: number; version_id: string;
    sections: FormSection[]; fields: FormField[];
  };
}

export function ServiceRequestForm({ slug, businessName, summary }: {
  slug: string;
  businessName: string;
  summary: { name: string; instructions: string | null };
}) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [load, setLoad] = useState<{ kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; data: Loaded } | { kind: 'error'; message: string }>({ kind: 'idle' });
  const [about, setAbout] = useState({ name: '', phone: '', email: '' });
  const [values, setValues] = useState<Record<string, AnswerValue>>({});
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);

  // The questions are fetched when the customer opens the card, once:
  // a page that lists twenty services should not load a form nobody
  // asked for. Started from the click rather than an effect keyed on
  // the load state, which would cancel itself the moment it began.
  const started = useRef(false);
  function start() {
    setOpen(true);
    if (started.current) return;
    started.current = true;
    setLoad({ kind: 'loading' });
    api<Loaded>(`/public/${encodeURIComponent(slug)}/service-request`)
      .then((data) => setLoad({ kind: 'ready', data }))
      .catch((e: unknown) => {
        started.current = false;
        setLoad({ kind: 'error', message: e instanceof ApiRequestError ? e.message : t('page.something_wrong') });
      });
  }

  const data = load.kind === 'ready' ? load.data : null;
  const fields = useMemo(() => (data?.form.fields ?? []).slice().sort((a, b) => a.position - b.position), [data]);
  const sections = useMemo(() => (data?.form.sections ?? []).slice().sort((a, b) => a.position - b.position), [data]);

  const set = useCallback((id: string, value: AnswerValue) => {
    setValues((current) => ({ ...current, [id]: value }));
    setIssues((current) => (current[id] ? { ...current, [id]: '' } : current));
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy || !data) return;
    const found = validateAnswers(fields, values);
    if (!about.name.trim()) found.__name = t('form.required');
    if (!about.phone.trim() && !about.email.trim()) found.__contact = t('request.contact_note');
    if (Object.keys(found).length > 0) {
      setIssues(found);
      queueMicrotask(() => summaryRef.current?.focus());
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      await api(`/public/${encodeURIComponent(slug)}/service-request`, {
        method: 'POST',
        json: {
          customer: { name: about.name.trim(), phone: about.phone.trim() || null, email: about.email.trim() || null, locale },
          form_version_id: data.form.version_id,
          answers: values,
        },
      });
      setDone(true);
    } catch (e) {
      setFailure(e instanceof ApiRequestError ? e.message : t('page.something_wrong'));
      if (e instanceof ApiRequestError && e.code === 'validation_failed' && Array.isArray(e.details)) {
        const next: Record<string, string> = {};
        for (const issue of e.details as { path: string; message: string }[]) next[issue.path.replace('answers.', '')] = issue.message;
        setIssues(next);
      }
    } finally {
      setBusy(false);
    }
  }

  const errorList = Object.entries(issues).filter(([, message]) => message);
  const labelFor = (id: string) => id === '__name' ? t('request.name') : id === '__contact' ? t('request.phone') : fields.find((f) => f.id === id)?.label ?? '';

  return (
    <section className="card service-request" id="request" aria-labelledby="request-h">
      <h2 id="request-h">{summary.name || t('request.heading')}</h2>
      <p>{summary.instructions || t('request.intro')}</p>

      {done ? (
        <div className="service-request__done" role="status">
          <h3>{t('request.done_title')}</h3>
          <p>{t('request.done_body', { name: businessName })}</p>
          <button type="button" className="btn btn-secondary" onClick={() => { setDone(false); setValues({}); setIssues({}); }}>
            {t('request.another')}
          </button>
        </div>
      ) : !open ? (
        <button type="button" className="btn btn-primary" onClick={start}>{t('request.start')}</button>
      ) : load.kind === 'loading' || load.kind === 'idle' ? (
        <p className="muted loading-note">{t('page.loading')}</p>
      ) : load.kind === 'error' ? (
        <p className="error" role="alert">{load.message || t('request.not_available')}</p>
      ) : (
        <form onSubmit={submit} noValidate>
          {errorList.length > 0 && (
            <div className="form-errors" role="alert" tabIndex={-1} ref={summaryRef}>
              <p>{t('form.fix_errors')}</p>
              <ul>{errorList.map(([id, message]) => <li key={id}>{labelFor(id)} — {message}</li>)}</ul>
            </div>
          )}
          {failure && <p className="error" role="alert">{failure}</p>}

          <h3 className="form-field-heading">{t('request.about_you')}</h3>
          <div className="field">
            <label htmlFor="sr-name">{t('request.name')} <span className="muted small">{t('form.required')}</span></label>
            <input id="sr-name" name="name" type="text" autoComplete="name" required value={about.name}
                   aria-invalid={issues.__name ? true : undefined}
                   onChange={(e) => { setAbout({ ...about, name: e.target.value }); setIssues((c) => ({ ...c, __name: '' })); }} />
            {issues.__name && <p className="error">{issues.__name}</p>}
          </div>
          <div className="field">
            <label htmlFor="sr-phone">{t('request.phone')}</label>
            <input id="sr-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" value={about.phone}
                   onChange={(e) => { setAbout({ ...about, phone: e.target.value }); setIssues((c) => ({ ...c, __contact: '' })); }} />
          </div>
          <div className="field">
            <label htmlFor="sr-email">{t('request.email')}</label>
            <input id="sr-email" name="email" type="email" autoComplete="email" inputMode="email" value={about.email}
                   onChange={(e) => { setAbout({ ...about, email: e.target.value }); setIssues((c) => ({ ...c, __contact: '' })); }} />
            <p className="hint">{t('request.contact_note')}</p>
            {issues.__contact && <p className="error">{issues.__contact}</p>}
          </div>

          {sections.map((section) => (
            <div key={section.id}>
              <h3 className="form-field-heading">{section.title}</h3>
              {section.description && <p className="muted small">{section.description}</p>}
              {fields.filter((f) => f.section_id === section.id).map((field) => (
                <FormQuestion key={field.id} field={field} value={values[field.id] ?? null} issue={issues[field.id]} onChange={set} />
              ))}
            </div>
          ))}
          {fields.filter((f) => !f.section_id).map((field) => (
            <FormQuestion key={field.id} field={field} value={values[field.id] ?? null} issue={issues[field.id]} onChange={set} />
          ))}

          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t('request.sending') : t('request.submit')}</button>
          <p className="muted small">{data!.form.privacy_note}</p>
        </form>
      )}
    </section>
  );
}
